import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { makeApi } from "@/lib/api";
import { ApptLine } from "@/components/loads/ApptLine";
import { ChevronRight, Truck, CheckCircle2, Camera, MapPin, Flag, History } from "lucide-react";

const TABS = [["active", "Active"], ["previous", "Previous"]];

export default function DriverLoads() {
  const { getToken } = useAuth();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "previous" ? "previous" : "active";
  useEffect(() => {
    (async () => {
      try {
        const api = makeApi(getToken);
        const { data } = await api.get("/loads");
        setLoads(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken]);

  const active = loads.filter((l) => !l.finished);
  const previous = loads.filter((l) => l.finished);
  const shown = tab === "active" ? active : previous;
  const counts = { active: active.length, previous: previous.length };

  return (
    <div className="space-y-5" data-testid="driver-loads-page">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>My loads</h1>
        <p className="text-sm text-slate-500 mt-0.5">{tab === "active" ? "Tap a load to upload POD photos." : "Finished loads. Tap one to see its documents."}</p>
      </div>

      <div className="grid grid-cols-2 rounded-full bg-slate-100 p-1" role="tablist" data-testid="driver-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k}
            onClick={() => setParams(k === "active" ? {} : { tab: k }, { replace: true })}
            className={`py-2 rounded-full text-sm font-medium transition-colors ${tab === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            data-testid={`driver-tab-${k}`}>
            {label} <span className="text-slate-400 tabular-nums" data-testid={`driver-tab-${k}-count`}>{loading ? "" : counts[k]}</span>
          </button>
        ))}
      </div>

      {loading && <div className="text-slate-500">Loading…</div>}

      {!loading && shown.length === 0 && (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center" data-testid={`driver-empty-${tab}`}>
          {tab === "active" ? <Truck className="w-10 h-10 mx-auto text-slate-400" /> : <History className="w-10 h-10 mx-auto text-slate-400" />}
          <div className="mt-3 font-medium">{tab === "active" ? "No active loads right now." : "No previous loads yet."}</div>
          <p className="mt-1 text-sm text-slate-500">{tab === "active" ? "Your dispatcher will assign you loads here." : "Delivered loads move here automatically."}</p>
        </div>
      )}

      {!loading && shown.length > 0 && (
        <div className="space-y-3" data-testid={`driver-list-${tab}`}>
          {shown.map((l) => <DriverLoadCard key={l.id} load={l} />)}
        </div>
      )}
    </div>
  );
}

function DriverLoadCard({ load }) {
  return (
    <Link
      to={`/driver/loads/${load.id}`}
      className={`block rounded-2xl border border-slate-200 bg-white p-4 active:bg-slate-50 ${load.finished ? "opacity-90" : ""}`}
      data-testid={`driver-load-${load.load_number}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-slate-500">Load #{load.carrier_number || load.load_number}{load.carrier_number && ` · Ref #${load.load_number}`}</div>
          <div className="font-semibold text-lg truncate" style={{ fontFamily: "Outfit" }}>{load.broker_name}</div>
          <div className="mt-2 space-y-1 text-sm text-slate-700">
            <div className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#002FA7]" /><span className="min-w-0">{load.pickup_location}<span className="block"><ApptLine date={load.pickup_date} appt={load.pickup_appt} testId={`driver-card-pickup-appt-${load.id}`} /></span></span></div>
            <div className="flex gap-2"><Flag className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" /><span className="min-w-0">{load.delivery_location}<span className="block"><ApptLine date={load.delivery_date} appt={load.delivery_appt} testId={`driver-card-delivery-appt-${load.id}`} /></span></span></div>
          </div>
          <div className="mt-3">
            {load.finished ? (
              <span className="pill bg-slate-100 text-slate-600 border border-slate-200" data-testid={`driver-load-done-${load.load_number}`}>
                <CheckCircle2 className="w-3 h-3" /> Completed{load.has_pod ? " · POD uploaded" : ""}
              </span>
            ) : load.has_pod ? (
              <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle2 className="w-3 h-3" /> POD uploaded
              </span>
            ) : (
              <span className="pill bg-blue-50 text-blue-700 border border-blue-200">
                <Camera className="w-3 h-3" /> Scan POD at delivery
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 mt-6" />
      </div>
    </Link>
  );
}
